import { useMemo, useState } from "react";
import { Link } from "react-router";
import { ArrowRight, FileText, Plus, Wallet } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/components/ui/tabs";
import { cn, formatCurrencyEUR, formatRelative } from "@/lib/utils";
import { useMdfAllocations } from "./use-mdf-allocations";
import { useMdfRequestsList } from "./use-mdf-requests-list";
import { MdfStatusBadge } from "./mdf-status-badge";
import type { MdfRequestRow, MdfRequestStatus } from "./types";

// Port of /app/portal/mdf/page.tsx, expanded per the user-approved
// sub-bucket 3 design: status tab strip + 3-card aggregate row + request
// card list. Legacy was allocation cards + history table — replaced
// because bucket 9 introduced drafts (a new partner-visible concept) and
// the tab structure surfaces them naturally alongside submitted requests.

type Segment = "drafts" | "pending" | "approved" | "closed";

const SEGMENTS: ReadonlyArray<{
  id: Segment;
  label: string;
  statuses: MdfRequestStatus[];
  emptyTitle: string;
  emptyBody: string;
}> = [
  {
    id: "drafts",
    label: "Drafts",
    statuses: ["draft"],
    emptyTitle: "No drafts in progress",
    emptyBody: "Start a new request to draft one — auto-save keeps your work.",
  },
  {
    id: "pending",
    label: "Pending",
    statuses: ["pending"],
    emptyTitle: "No pending requests",
    emptyBody: "Anything you submit will appear here while it's under review.",
  },
  {
    id: "approved",
    label: "Approved",
    statuses: ["approved", "spent"],
    emptyTitle: "No approved requests yet",
    emptyBody: "Once a request is approved it will show here.",
  },
  {
    id: "closed",
    label: "Closed",
    statuses: ["declined", "cancelled"],
    emptyTitle: "No closed requests",
    emptyBody: "Declined and cancelled requests land here for reference.",
  },
];

export function MdfListPage() {
  const allocations = useMdfAllocations();
  const requests = useMdfRequestsList();

  const allRequests = requests.data ?? [];
  const counts = useMemo(() => countByStatus(allRequests), [allRequests]);
  const aggregates = useMemo(() => aggregate(allRequests), [allRequests]);

  // Default tab: pending if any pending, else drafts if any drafts, else
  // pending as the conventional landing.
  const defaultSegment: Segment = counts.pending > 0
    ? "pending"
    : counts.drafts > 0
      ? "drafts"
      : "pending";

  const [segment, setSegment] = useState<Segment>(defaultSegment);

  const hasActiveAllocation = (allocations.data ?? []).length > 0;
  const newButtonDisabled =
    !allocations.isLoading && !hasActiveAllocation;

  return (
    <div>
      <header className="mb-7 flex items-start justify-between gap-4">
        <div>
          <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
            Marketing funds
          </p>
          <h1 className="mt-1 font-display text-3xl font-semibold tracking-tight">
            Marketing fund requests
          </h1>
          <p className="mt-1 max-w-2xl text-sm text-muted-foreground">
            Draft, submit, and track requests against your active allocations.
          </p>
        </div>
        <Button asChild disabled={newButtonDisabled}>
          {newButtonDisabled ? (
            <span className="pointer-events-none inline-flex items-center gap-1.5 opacity-60">
              <Plus className="h-4 w-4" />
              Request funds
            </span>
          ) : (
            <Link to="/portal/mdf/new" className="inline-flex items-center gap-1.5">
              <Plus className="h-4 w-4" />
              Request funds
            </Link>
          )}
        </Button>
      </header>

      {/* Aggregate row. Always visible — partners want a quick budget read. */}
      <div className="mb-8 grid gap-3 sm:grid-cols-3">
        <AggregateCard
          label="Requested"
          value={formatCurrencyEUR(aggregates.requestedEur)}
          hint={`${aggregates.requestedCount} request${aggregates.requestedCount === 1 ? "" : "s"}`}
        />
        <AggregateCard
          label="Approved"
          value={formatCurrencyEUR(aggregates.approvedEur)}
          hint={
            aggregates.approvedCount > 0
              ? `${aggregates.approvedCount} approved`
              : "Pending review"
          }
        />
        <AggregateCard
          label="Spent"
          value={formatCurrencyEUR(aggregates.spentEur)}
          hint={
            aggregates.spentCount > 0
              ? `${aggregates.spentCount} closed out`
              : "Awaiting spend"
          }
        />
      </div>

      <Tabs
        value={segment}
        onValueChange={(v) => setSegment(v as Segment)}
        className="w-full"
      >
        <TabsList>
          {SEGMENTS.map((s) => (
            <TabsTrigger key={s.id} value={s.id}>
              {s.label}
              <span className="ml-1.5 text-[11px] text-muted-foreground">
                {counts[s.id]}
              </span>
            </TabsTrigger>
          ))}
        </TabsList>

        {SEGMENTS.map((s) => {
          const rows = allRequests.filter((r) => s.statuses.includes(r.status));
          return (
            <TabsContent key={s.id} value={s.id} className="mt-6">
              {requests.isLoading ? (
                <SkeletonList />
              ) : requests.isError ? (
                <ErrorPanel />
              ) : rows.length === 0 ? (
                <EmptyPanel
                  title={s.emptyTitle}
                  body={s.emptyBody}
                  ctaDisabled={newButtonDisabled}
                />
              ) : (
                <ul className="space-y-3">
                  {rows.map((row) => (
                    <li key={row.id}>
                      <RequestCard row={row} />
                    </li>
                  ))}
                </ul>
              )}
            </TabsContent>
          );
        })}
      </Tabs>

      {!allocations.isLoading && !hasActiveAllocation ? (
        <div className="mt-8 rounded-xl border bg-muted/30 p-5 text-sm text-muted-foreground">
          <Wallet className="mb-2 h-5 w-5 text-muted-foreground" />
          You don&rsquo;t have an active marketing fund right now. When one is
          set up, you&rsquo;ll be able to request budget here.
        </div>
      ) : null}
    </div>
  );
}

function AggregateCard({
  label,
  value,
  hint,
}: {
  label: string;
  value: string;
  hint: string;
}) {
  return (
    <div className="rounded-xl border bg-card p-4">
      <p className="text-[11px] font-medium uppercase tracking-wider text-muted-foreground">
        {label}
      </p>
      <p className="mt-1 font-display text-2xl font-semibold tracking-tight tabular-nums">
        {value}
      </p>
      <p className="mt-1 text-xs text-muted-foreground">{hint}</p>
    </div>
  );
}

function RequestCard({ row }: { row: MdfRequestRow }) {
  return (
    <Link
      to={`/portal/mdf/${row.id}`}
      className="group flex items-center justify-between gap-4 rounded-xl border bg-card p-4 transition hover:border-accent"
    >
      <div className="min-w-0">
        <div className="flex items-center gap-2">
          <p className="truncate font-medium text-foreground">
            {row.campaign_name ?? "Untitled draft"}
          </p>
          <MdfStatusBadge status={row.status} />
        </div>
        <p className="mt-1 text-xs text-muted-foreground">
          {row.campaign_type ? `${row.campaign_type} · ` : ""}
          {row.status === "draft" ? "Last edited" : "Submitted"}{" "}
          {formatRelative(row.status === "draft" ? row.updated_at : row.created_at)}
        </p>
      </div>
      <div className="flex shrink-0 items-center gap-3">
        <span className="font-display text-base font-semibold tabular-nums">
          {row.amount_eur != null ? formatCurrencyEUR(row.amount_eur) : "—"}
        </span>
        <ArrowRight className="h-4 w-4 text-muted-foreground transition group-hover:translate-x-0.5 group-hover:text-accent" />
      </div>
    </Link>
  );
}

function SkeletonList() {
  return (
    <ul className="space-y-3">
      {Array.from({ length: 3 }).map((_, i) => (
        <li key={i}>
          <Skeleton className="h-20 rounded-xl" />
        </li>
      ))}
    </ul>
  );
}

function EmptyPanel({
  title,
  body,
  ctaDisabled,
}: {
  title: string;
  body: string;
  ctaDisabled: boolean;
}) {
  return (
    <section className="rounded-xl border bg-card p-10 text-center">
      <FileText className="mx-auto h-8 w-8 text-muted-foreground" />
      <h2 className="mt-3 font-display text-base font-semibold tracking-tight">
        {title}
      </h2>
      <p className="mt-1 text-sm text-muted-foreground">{body}</p>
      {!ctaDisabled ? (
        <Link
          to="/portal/mdf/new"
          className={cn(
            "mt-4 inline-flex items-center gap-1 text-xs font-medium text-accent hover:underline",
          )}
        >
          Start a request
          <ArrowRight className="h-3 w-3" />
        </Link>
      ) : null}
    </section>
  );
}

function ErrorPanel() {
  return (
    <section className="rounded-xl border bg-card p-10 text-center">
      <FileText className="mx-auto h-8 w-8 text-muted-foreground" />
      <h2 className="mt-3 font-display text-base font-semibold tracking-tight">
        Couldn&rsquo;t load requests
      </h2>
      <p className="mt-1 text-sm text-muted-foreground">
        Please refresh the page. If the problem persists, contact support.
      </p>
    </section>
  );
}

function countByStatus(rows: MdfRequestRow[]): Record<Segment, number> {
  const out: Record<Segment, number> = {
    drafts: 0,
    pending: 0,
    approved: 0,
    closed: 0,
  };
  for (const r of rows) {
    if (r.status === "draft") out.drafts++;
    else if (r.status === "pending") out.pending++;
    else if (r.status === "approved" || r.status === "spent") out.approved++;
    else if (r.status === "declined" || r.status === "cancelled") out.closed++;
  }
  return out;
}

function aggregate(rows: MdfRequestRow[]): {
  requestedEur: number;
  requestedCount: number;
  approvedEur: number;
  approvedCount: number;
  spentEur: number;
  spentCount: number;
} {
  let requestedEur = 0;
  let requestedCount = 0;
  let approvedEur = 0;
  let approvedCount = 0;
  let spentEur = 0;
  let spentCount = 0;
  for (const r of rows) {
    const amount = r.amount_eur ?? 0;
    if (r.status !== "draft" && r.status !== "cancelled") {
      requestedEur += amount;
      requestedCount++;
    }
    if (r.status === "approved" || r.status === "spent") {
      approvedEur += amount;
      approvedCount++;
    }
    if (r.status === "spent") {
      spentEur += amount;
      spentCount++;
    }
  }
  return {
    requestedEur,
    requestedCount,
    approvedEur,
    approvedCount,
    spentEur,
    spentCount,
  };
}
